// Wave TW-1.f.1 (2026-05-24) — 외부 바이어 거래 완료 확인 페이지
// API: GET /buyer-app/transactions/pending → { items: [tx_confirm rows] }
//      POST /buyer-app/transactions/:id/confirm → { completed, data }
//
// 흐름:
//   1) mount + 60s polling + visibilitychange (BidsList 패턴 재사용)
//   2) pending 거래 카드 — source 정보 + 양측 확인 상태 + "거래 완료 확인" 버튼
//   3) 클릭 → POST confirm → list reload → 카드 사라짐 (completed_at set 시)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { confirm } from './ConfirmDialog';
import { showToast } from './Toast';
import { useT } from '../../i18n/I18nProvider';

function AuctionBuyerTransactions() {
  var navigate = useNavigate();
  var t = useT();
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [busyId, setBusyId] = useState(0);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/transactions/pending', {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) {
          setItems(d.data.items || []);
        } else {
          setError(displayApiError(d, t, 'errListLoad'));
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork'));
        setLoading(false);
      });
  }, [navigate, t]);

  useEffect(function() { load(); }, [load]);

  // 자동 reload — BidsList 패턴 재사용 (60s polling + visibilitychange)
  useEffect(function() {
    var timer = setInterval(function() {
      if (document.visibilityState === 'visible') load();
    }, 60000);
    var onVis = function() {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return function() {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  // Wave TW-1.f.8 — 분쟁 신고 (window.prompt 사용 — 외부 바이어 측 신속 입력 우선)
  var onDispute = function(row) {
    var reason = window.prompt(t('txDisputePromptTitle') + '\n\n' + t('txDisputeReasonPlaceholder'), '');
    if (reason === null) return;
    reason = String(reason).trim();
    setBusyId(row.id);
    authBuyerFetch(API + '/buyer-app/transactions/' + row.id + '/dispute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason }),
    })
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        setBusyId(0);
        if (d && d.success) {
          showToast(t('txDisputeToastDone'), { variant: 'success' });
          load();
        } else {
          showToast(displayApiError(d, t, 'txDisputeToastFail'), { variant: 'error' });
        }
      })
      .catch(function(e) {
        setBusyId(0);
        if (!(e && e.message === 'unauthorized')) showToast(t('commonNetwork'), { variant: 'error' });
      });
  };

  var onConfirm = function(row) {
    confirm({ message: t('txConfirmPrompt'), variant: 'primary' }).then(function(ok) {
      if (!ok) return;
      setBusyId(row.id);
      authBuyerFetch(API + '/buyer-app/transactions/' + row.id + '/confirm', { method: 'POST' })
        .then(function(r) {
          if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
          return r.json();
        })
        .then(function(d) {
          setBusyId(0);
          if (d && d.success) {
            showToast(d.completed ? t('txConfirmToastBoth') : t('txConfirmToastWait'), { variant: 'success' });
            load();
          } else {
            showToast(displayApiError(d, t, 'errConfirmFailed'), { variant: 'error' });
          }
        })
        .catch(function(e) {
          setBusyId(0);
          if (!(e && e.message === 'unauthorized')) showToast(t('commonNetwork'), { variant: 'error' });
        });
    });
  };

  return (
    <AuctionBuyerLayout activeKey="transactions">
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px 80px' }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>📋 {t('txConfirmTitle')}</h2>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {t('txConfirmSubtitle')}
          </div>
        </div>

        {loading && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            {t('commonLoading')}
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{error}</div>
            <button type="button" onClick={load}
              style={{ padding: '8px 16px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
              {t('commonRetry')}
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <EmptyState icon="✅" title={t('txConfirmEmpty')} />
        )}

        {!loading && !error && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map(function(row) {
              var sourceLabel = row.source_type === 'rfq' ? t('txConfirmSourceRfq') : t('txConfirmSourceAuction');
              var alreadyConfirmed = !!row.buyer_confirmed_at;
              var sellerConfirmed = !!row.seller_confirmed_at;
              return (
                <div key={row.id}
                  style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1B3A5C', background: '#eff6ff', padding: '3px 8px', borderRadius: 6 }}>
                      {sourceLabel} #{row.source_id}
                    </span>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {(row.created_at || '').slice(0, 10)}
                    </span>
                  </div>
                  {row.amount && (
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                      {Number(row.amount).toLocaleString()}원
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#475569' }}>
                    <span style={{ color: sellerConfirmed ? '#15803d' : '#94a3b8' }}>
                      {sellerConfirmed ? t('txConfirmSellerConfirmed') : t('txConfirmSellerPending')}
                    </span>
                    <span style={{ color: alreadyConfirmed ? '#15803d' : '#94a3b8' }}>
                      {alreadyConfirmed ? t('txConfirmBuyerConfirmed') : t('txConfirmBuyerPending')}
                    </span>
                  </div>
                  {/* Wave TW-1.f.8 — 분쟁 신고된 거래는 뱃지 표시, 버튼 모두 disable */}
                  {row.disputed_at ? (
                    <div style={{ padding: '8px 12px', background: '#fee2e2', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#991b1b' }}>
                      {t('txDisputeBadge')}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button"
                        disabled={alreadyConfirmed || busyId === row.id}
                        onClick={function() { onConfirm(row); }}
                        style={{
                          flex: 2, padding: '10px 0',
                          background: alreadyConfirmed ? '#e2e8f0' : '#1B3A5C',
                          color: alreadyConfirmed ? '#64748b' : 'white',
                          border: 'none', borderRadius: 8,
                          fontSize: 13, fontWeight: 700,
                          cursor: alreadyConfirmed ? 'default' : 'pointer',
                        }}>
                        {alreadyConfirmed ? t('txConfirmCompleted') : (busyId === row.id ? t('txConfirmBusy') : t('txConfirmActionLabel'))}
                      </button>
                      <button type="button"
                        disabled={busyId === row.id}
                        onClick={function() { onDispute(row); }}
                        style={{
                          flex: 1, padding: '10px 0',
                          background: 'white', color: '#dc2626',
                          border: '1.5px solid #fecaca', borderRadius: 8,
                          fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        }}>
                        🚨 {t('txDisputeAction')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerTransactions;
